import { useEffect, useMemo, useState } from "react";
import { ChevronLeft, ChevronRight, Plus, AlertTriangle } from "lucide-react";
import { adminBookings } from "@/lib/api";
import { statusMeta, fmtDate } from "./constants";

const monthKey = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
const dayKey = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
const monthName = (d) => d.toLocaleDateString("en-AU", { month: "long", year: "numeric" });

export default function Diary({ token, onEdit, refreshKey }) {
  const [cursor, setCursor] = useState(() => { const d = new Date(); d.setDate(1); return d; });
  const [bookings, setBookings] = useState([]);
  const [selectedDay, setSelectedDay] = useState(null);

  useEffect(() => {
    adminBookings(token, { month: monthKey(cursor) }).then((d) => setBookings(d.bookings || [])).catch(() => {});
  }, [cursor, token, refreshKey]);

  const byDay = useMemo(() => {
    const m = new Map();
    bookings.forEach((b) => { const list = m.get(b.event_date) || []; list.push(b); m.set(b.event_date, list); });
    return m;
  }, [bookings]);

  const grid = useMemo(() => {
    const first = new Date(cursor.getFullYear(), cursor.getMonth(), 1);
    const startWeekday = first.getDay(); // 0 Sun
    const daysInMonth = new Date(cursor.getFullYear(), cursor.getMonth() + 1, 0).getDate();
    const cells = [];
    for (let i = 0; i < startWeekday; i++) cells.push(null);
    for (let d = 1; d <= daysInMonth; d++) cells.push(new Date(cursor.getFullYear(), cursor.getMonth(), d));
    while (cells.length % 7 !== 0) cells.push(null);
    return cells;
  }, [cursor]);

  const today = dayKey(new Date());
  const selectedEvents = selectedDay ? (byDay.get(selectedDay) || []) : [];

  return (
    <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
      <div className="lg:col-span-2 rounded-3xl bg-white border border-sand p-5 md:p-6">
        <div className="flex items-center justify-between mb-5">
          <div className="flex items-center gap-2">
            <button data-testid="cal-prev" onClick={() => setCursor(new Date(cursor.getFullYear(), cursor.getMonth() - 1, 1))} className="w-9 h-9 rounded-full border border-sand hover:border-coral grid place-items-center"><ChevronLeft className="w-4 h-4 text-ocean" /></button>
            <button data-testid="cal-next" onClick={() => setCursor(new Date(cursor.getFullYear(), cursor.getMonth() + 1, 1))} className="w-9 h-9 rounded-full border border-sand hover:border-coral grid place-items-center"><ChevronRight className="w-4 h-4 text-ocean" /></button>
          </div>
          <h3 className="font-serif text-xl md:text-2xl text-ocean-deep" data-testid="cal-month">{monthName(cursor)}</h3>
          <button data-testid="cal-today" onClick={() => { const d = new Date(); d.setDate(1); setCursor(d); }} className="text-xs overline text-ocean hover:text-coral">Today</button>
        </div>

        <div className="grid grid-cols-7 gap-1 text-center overline text-[10px] text-ocean/50 mb-2">
          {["Sun","Mon","Tue","Wed","Thu","Fri","Sat"].map((d) => <div key={d}>{d}</div>)}
        </div>

        <div className="grid grid-cols-7 gap-1">
          {grid.map((d, i) => {
            if (!d) return <div key={i} className="h-20 md:h-24 rounded-lg bg-cream/60" />;
            const key = dayKey(d);
            const evts = byDay.get(key) || [];
            const isToday = key === today;
            const isSelected = key === selectedDay;
            const hasConflict = evts.length >= 2;
            return (
              <button
                key={i}
                data-testid={`cal-day-${d.getDate()}`}
                onClick={() => setSelectedDay(key)}
                className={`h-20 md:h-24 rounded-lg border p-1.5 text-left transition-colors
                  ${isSelected ? "bg-ocean-deep text-white border-ocean-deep" : hasConflict ? "bg-amber-50 border-amber-300" : "bg-cream border-sand hover:border-coral"}`}
              >
                <div className="flex items-start justify-between">
                  <span className={`text-xs ${isToday && !isSelected ? "bg-coral text-white rounded-full w-5 h-5 grid place-items-center" : ""}`}>{d.getDate()}</span>
                  {hasConflict && !isSelected && <AlertTriangle className="w-3 h-3 text-amber-600" />}
                </div>
                <div className="mt-1 space-y-0.5">
                  {evts.slice(0, 3).map((b) => (
                    <div key={b.id} className="flex items-center gap-1">
                      <span className="w-1.5 h-1.5 rounded-full shrink-0" style={{ background: statusMeta(b.status).dot }} />
                      <span className={`text-[10px] truncate ${isSelected ? "text-white/90" : "text-ocean-deep"}`}>{b.customer_name || b.event_type}</span>
                    </div>
                  ))}
                  {evts.length > 3 && <span className={`text-[9px] ${isSelected ? "text-white/70" : "text-ocean/50"}`}>+{evts.length - 3} more</span>}
                </div>
              </button>
            );
          })}
        </div>
      </div>

      <div className="rounded-3xl bg-white border border-sand p-6 h-fit">
        <div className="flex justify-between items-center mb-4">
          <h3 className="font-serif text-xl text-ocean-deep">{selectedDay ? fmtDate(selectedDay) : "Pick a day"}</h3>
          <button data-testid="diary-new" onClick={() => onEdit({ event_date: selectedDay || dayKey(new Date()) })} className="rounded-full bg-coral text-white px-4 py-2 text-xs overline hover:bg-[#c9633f] flex items-center gap-1.5"><Plus className="w-3.5 h-3.5" />New</button>
        </div>
        {selectedDay && selectedEvents.length >= 2 && (
          <div className="rounded-xl bg-amber-50 border border-amber-200 p-3 text-xs text-amber-900 mb-4 flex items-start gap-2"><AlertTriangle className="w-4 h-4 mt-0.5 shrink-0" /> Multiple bookings on this date — only one bartender.</div>
        )}
        {!selectedDay && <p className="text-sm text-ocean/50">Tap a day to see events, or hit <b>New</b> to add one.</p>}
        {selectedDay && selectedEvents.length === 0 && <p className="text-sm text-ocean/50">No events this day.</p>}
        <div className="space-y-3">
          {selectedEvents.map((b) => {
            const s = statusMeta(b.status);
            return (
              <button key={b.id} onClick={() => onEdit(b)} className="w-full text-left rounded-2xl bg-cream border border-sand hover:border-coral p-4 transition-colors">
                <div className="flex justify-between items-start">
                  <p className="font-medium text-ocean-deep">{b.customer_name || "—"}</p>
                  <span className={`text-[10px] overline rounded-full px-2.5 py-1 ${s.bg} ${s.text}`}>{s.label}</span>
                </div>
                <p className="text-xs text-ocean/70 mt-1">{b.event_type} · {b.guest_count} guests</p>
                {b.location && <p className="text-xs text-ocean/50 mt-0.5">{b.location}</p>}
              </button>
            );
          })}
        </div>
      </div>
    </div>
  );
}
